import React, { useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

import { DatePicker, ConfigProvider } from "antd";
import { RightCircleOutlined, LeftCircleOutlined } from "@ant-design/icons";
import { usePeriodSelector } from "../../hooks/usePeriodSelector";
import "./PeriodSelector.css";

const { RangePicker } = DatePicker;
const dateFormat = "DD MMM YYYY";
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

// Memoize static icons
const lockIcon = icon({ name: "lock", family: "sharp", style: "solid" });

export const PeriodSelector = (props) => {
	const { 
		reportTimePeriod, 
		onTimePeriodChange, 
		isEditMode,
		showTimePeriodPrompt,
		onApplyTimePeriodToReport,
		onDismissTimePeriodPrompt
	} = props;
	
	// Track if calendar was opened (to detect when user selects dates)
	const calendarWasOpenRef = useRef(false);
	// Track previous period to detect changes
	const previousPeriodRef = useRef(null);
	
	// Use the custom hook to get all period selector functionality
	const {
		rangePickerValue,
		changePeriod,
		rangePresets,
		handleDateChange: originalHandleDateChange
	} = usePeriodSelector(props);

	// Detect period changes when rangePickerValue updates
	useEffect(() => {
		if (isEditMode && onTimePeriodChange && rangePickerValue?.[0] && rangePickerValue?.[1]) {
			const currentPeriod = {
				startDate: rangePickerValue[0].format('YYYY-MM-DD'),
				endDate: rangePickerValue[1].format('YYYY-MM-DD')
			};
			
			// Compare with previous period
			if (previousPeriodRef.current) {
				const previousPeriod = previousPeriodRef.current;
				if (
					previousPeriod.startDate !== currentPeriod.startDate ||
					previousPeriod.endDate !== currentPeriod.endDate
				) {
					// Period changed - notify callback
					onTimePeriodChange(rangePickerValue[0], rangePickerValue[1]);
				}
			}
			
			// Update previous period reference
			previousPeriodRef.current = currentPeriod;
		}
	}, [rangePickerValue, isEditMode, onTimePeriodChange]);

	// Wrapped date change handler that detects changes in edit mode
	const handleDateChange = useCallback((dates) => {
		originalHandleDateChange(dates);
		
		// Always show prompt when dates are selected in edit mode
		if (isEditMode && dates?.[0] && dates?.[1] && onTimePeriodChange) {
			onTimePeriodChange(dates[0], dates[1]);
		}
	}, [originalHandleDateChange, isEditMode, onTimePeriodChange]);

	// Handle calendar date selection (fires even when same dates are selected)
	const handleCalendarChange = useCallback((dates) => {
		// Always show prompt when dates are selected in the calendar in edit mode
		if (isEditMode && dates?.[0] && dates?.[1] && onTimePeriodChange) {
			onTimePeriodChange(dates[0], dates[1]);
		}
	}, [isEditMode, onTimePeriodChange]);

	// Handle calendar open/close - show prompt when calendar closes after being opened
	const handleOpenChange = useCallback((open) => {
		if (open) {
			// Calendar opened - track this
			calendarWasOpenRef.current = true;
		} else {
			// Calendar closed - if it was opened and we're in edit mode, show prompt
			if (calendarWasOpenRef.current && isEditMode && onTimePeriodChange) {
				// Get current dates from rangePickerValue
				if (rangePickerValue?.[0] && rangePickerValue?.[1]) {
					onTimePeriodChange(rangePickerValue[0], rangePickerValue[1]);
				}
			}
			calendarWasOpenRef.current = false;
		}
	}, [isEditMode, onTimePeriodChange, rangePickerValue]);

	// Period navigation handler
	const handleChangePeriod = useCallback((direction) => {
		changePeriod(direction);
	}, [changePeriod]);

	// Check if current period matches report period
	const isFromReport = reportTimePeriod?.start_date && 
		rangePickerValue?.[0] && 
		rangePickerValue?.[1] &&
		rangePickerValue[0].format('YYYY-MM-DD') === reportTimePeriod.start_date &&
		rangePickerValue[1].format('YYYY-MM-DD') === reportTimePeriod.end_date;

	// Lock the period selector when viewing a report with a saved time period
	const isLocked = !isEditMode && reportTimePeriod?.start_date && reportTimePeriod?.end_date;

	// Compact mode drops the sub-toolbar padding/margin so the pill fits inside the
	// global TopBar (e.g. the report editor renders its controls in the app header).
	const compact = props.compact;

	return (
		<div className={`relative ${compact ? "" : "pt-4 pl-4 pr-4"}`}>
			<div
				className={`${compact ? "" : "mb-4"} text-text-3 px-4 pb-1 pt-1 h-[40px] w-[340px] rounded-full bg-side border text-sm ${
					isLocked
						? 'cursor-not-allowed border-primary bg-surface-2'
						: `cursor-pointer hover:border-border-strong ${isFromReport ? 'border-primary' : 'border-border'}`
				}`}
				title={isLocked ? "Time period is locked to the report's saved period. Edit the report to change the time period." : undefined}
			>
				<FontAwesomeIcon
					icon={icon({ name: "calendar-days", family: "sharp", style: "regular" })}
					className={`ml-0 relative top-[-1px] mr-4 w-[16px] ${isFromReport || isLocked ? 'text-primary' : ''}`}
				/>
				{!isLocked ? (
					<FontAwesomeIcon
						icon={icon({ name: "angle-left", family: "sharp", style: "regular" })}
						className="ml-0 relative top-[0px] cursor-pointer w-[16px]"
						onClick={() => handleChangePeriod("prev")}
					/>
				) : (
					<span className="w-[16px] inline-block" />
				)}
				<ConfigProvider
					theme={{
						token: {
							fontFamily: "Arimo",
							fontSize: 12,
							inputFontSize: 12,
						},
					}}
				>
					<RangePicker
						defaultValue={rangePickerValue}
						format={dateFormat}
						presets={rangePresets}
						variant="borderless"
						allowClear={false}
						inputReadOnly={true}
						suffixIcon={null}
						nextIcon={<RightCircleOutlined style={{ fontSize: "16px", paddingTop:"8px"}}/>}
						prevIcon={<LeftCircleOutlined style={{ fontSize: "16px", paddingTop:"8px" }}/>}
						superNextIcon={null }
						superPrevIcon={null}
						onChange={handleDateChange}
						onCalendarChange={handleCalendarChange}
						onOpenChange={handleOpenChange}
						value={rangePickerValue}
						disabled={isLocked}
						open={isLocked ? false : undefined}
					/>
				</ConfigProvider>
				{!isLocked ? (
					<FontAwesomeIcon
						icon={icon({
							name: "angle-right",
							family: "sharp",
							style: "regular",
						})}
						className="ml-2 relative top-[0px] cursor-pointer w-[16px]"
						onClick={() => handleChangePeriod("next")}
					/>
				) : (
					<FontAwesomeIcon
						icon={lockIcon}
						className="ml-2 relative top-[0px] w-[12px] text-text-2"
						title="Locked to report period"
					/>
				)}
			</div>
			
			{/* Report period indicator badge */}
			{(isFromReport || isLocked) && (
				<div className="absolute -top-1 left-28 px-2 py-0.5 rounded text-xs text-primary flex items-center gap-1">
					{isLocked && <FontAwesomeIcon icon={lockIcon} className="text-[9px]" />}
					Derived from report
				</div>
			)}
			
			{/* Time period prompt */}
			{showTimePeriodPrompt && (
				<div className="absolute top-full left-0 right-0 mt-1 mx-4 p-3 bg-primary-soft border border-primary rounded-lg shadow-float z-50">
					<p className="text-sm text-text mb-2">
						Apply this time range to the report?
					</p>
					<div className="flex gap-2">
						<button
							onClick={onApplyTimePeriodToReport}
							className="px-3 py-1 text-sm bg-primary text-white rounded hover:bg-primary-hover transition-colors"
						>
							Apply to report
						</button>
						<button
							onClick={onDismissTimePeriodPrompt}
							className="px-3 py-1 text-sm bg-card text-text-2 border border-border rounded hover:bg-surface-2 transition-colors"
						>
							Keep as preview
						</button>
					</div>
				</div>
			)}
		</div>
	);
};

PeriodSelector.propTypes = {
	startDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string, PropTypes.instanceOf(Date)]).isRequired,
	endDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string, PropTypes.instanceOf(Date)]).isRequired,
	setStartDate: PropTypes.func.isRequired,
	setEndDate: PropTypes.func.isRequired,
	/** Report time period object (for indicator) */
	reportTimePeriod: PropTypes.shape({
		start_date: PropTypes.string,
		end_date: PropTypes.string,
	}),
	/** Callback when time period changes in edit mode */
	onTimePeriodChange: PropTypes.func,
	/** Whether we're in edit mode */
	isEditMode: PropTypes.bool,
	/** Whether to show the time period prompt */
	showTimePeriodPrompt: PropTypes.bool,
	/** Callback to apply time period to report */
	onApplyTimePeriodToReport: PropTypes.func,
	/** Callback to dismiss time period prompt */
	onDismissTimePeriodPrompt: PropTypes.func,
	/** Render without the sub-toolbar padding so it fits in the global TopBar */
	compact: PropTypes.bool,
};
